import React, { useState } from 'react';
import { setSnackbar } from 'flow/actions/app-actions';
import { segmentTrack } from 'flow/actions/segment-actions';
import { removeTicketMember } from 'flow/actions/tickets-action';
import BaseListItem from 'flow/components/ui-components/BaseListItem';
import ExtensionContactAvatar from 'flow/components/ui-components/ExtensionContactAvatar';
import ExtensionName from 'flow/components/ui-components/ExtensionName';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    selectAssignee,
    selectMembersCount,
} from 'flow/selectors/TicketsSelectors';
import { ExtensionKey, TicketKey } from 'flow/types/EntityKeys';
import { twMerge } from 'tailwind-merge';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import useTicketAction from 'flow/hooks/useTicketAction';
import { ticketWriteWasRefused } from 'flow/utils/ticket-utils';

interface Props {
    memberKey: ExtensionKey;
    ticketKey: TicketKey;
    canEdit: boolean;
}

const AccessMemberItem = ({ memberKey, ticketKey, canEdit }: Props) => {
    const { i18next } = useAppContext();
    const { openModal } = useModal();
    const dispatch = useAppDispatch();
    const runTicketAction = useTicketAction();

    const assignee = useAppSelector((state) =>
        selectAssignee(state, ticketKey),
    );
    const membersCount = useAppSelector((state) =>
        selectMembersCount(state, ticketKey),
    );

    // The assignee has to stay, and a ticket without members would be
    // reachable by nobody once general access is off.
    const removeIsDisabled = assignee === memberKey || membersCount === 1;

    // The row goes quiet while its removal is in flight, so a second click cannot
    // ask again and the row stops offering the contact card it is about to lose.
    // A successful removal unmounts the row, so only a refusal lands back here.
    const [removalIsInFlight, setRemovalIsInFlight] = useState(false);

    const onClickRemove = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (removeIsDisabled || removalIsInFlight) return;

        setRemovalIsInFlight(true);
        runTicketAction(removeTicketMember(ticketKey, memberKey))
            .catch((error?: { status?: number }) => {
                // The refusal already opened the Access denied dialog, so the
                // snackbar is for everything else.
                if (ticketWriteWasRefused(error)) return;
                dispatch(setSnackbar({ i18nextMessage: 'snackbar.error' }));
            })
            .finally(() => setRemovalIsInFlight(false));
        dispatch(
            segmentTrack(TICKET_STRINGS.REMOVE_MEMBER, {
                component: 'ticket access modal',
            }),
        );
    };

    return (
        <BaseListItem
            className={twMerge(
                'px-3 py-1.5',
                removalIsInFlight && 'pointer-events-none opacity-50',
            )}
            onClick={() => {
                if (removalIsInFlight) return;
                openModal('contactModal', { extensionKey: memberKey });
            }}
        >
            <div className="flex grow items-center justify-between gap-x-2">
                <div className="flex min-w-0 items-center gap-x-2">
                    <ExtensionContactAvatar
                        extensionKey={memberKey}
                        size="small"
                        lazyLoad={true}
                    />
                    <div className="truncate text-sm text-gray-800">
                        <ExtensionName extensionKey={memberKey} />
                    </div>
                </div>
                {canEdit ? (
                    <IconButton
                        className="size-6 shrink-0 disabled:opacity-30"
                        onClick={onClickRemove}
                        disabled={removeIsDisabled || removalIsInFlight}
                        aria-label={i18next.t('Chat.Details.RemoveMember')}
                    >
                        <Icon iconKey="close" size="1rem" />
                    </IconButton>
                ) : null}
            </div>
        </BaseListItem>
    );
};

export default React.memo(AccessMemberItem);
